import {
  Account,
  AccountRoleAssignmentsUpdateRequest,
  AccountWithRoleAssignments,
  Device,
  Organization,
  Role,
  Sensor,
  Site,
  Study,
  StudyDataRecord,
  Subject,
} from '../models';
import {
  Device as DeviceEntity,
  EntityType,
  Organization as OrganizationEntity,
  Role as RoleEntity,
  Sensor as SensorEntity,
  Site as SiteEntity,
  Study as StudyEntity,
  Subject as SubjectEntity,
  StudyData as StudyDataEntity,
  ProductType,
  User as AccountEntity,
  UserRoleAssignmentsByEntity,
  UserRoleAssignmentsByEntityUpdate,
} from 'adc-sirius-module-common';

const EMPTY_UUID = '00000000-0000-0000-0000-000000000000';

export function mapOrganizationEntityToOrganization(
  entity: OrganizationEntity,
): Organization {
  return {
    key: entity.key,
    name: entity.name,
    description: entity.description,
    region: entity.region,
    createdBy: entity.c.created_by,
    createdOn: entity.c.created,
    updatedBy: entity.c.updated_by,
    updatedOn: entity.c.updated,
    updateReason: entity.c.update_reason,
    state: entity.row_state,
    type: entity.type,
  };
}

export function mapOrganizationToOrganizationEntity(
  organization: Organization,
): OrganizationEntity {
  return {
    key: organization.key,
    name: organization.name,
    description: organization.description || '',
    region: organization.region,
    c: {
      created_by: organization.createdBy,
      created: organization.createdOn,
      updated_by: organization.updatedBy || '',
      updated: organization.updatedOn || 0,
      update_reason: organization.updateReason || '',
    },
    row_state: organization.state,
    type: organization.type,
  };
}

export function mapStudyEntityToStudy(entity: StudyEntity): Study {
  return {
    key: entity.key,
    name: entity.name,
    description: entity.description,
    region: entity.region,
    createdBy: entity.c.created_by,
    createdOn: entity.c.created,
    updatedBy: entity.c.updated_by,
    updatedOn: entity.c.updated,
    updateReason: entity.c.update_reason,
    state: entity.row_state,
    status: entity.status,
    type: entity.type,
    configuration: entity.configuration,
    organizationKey:
      entity.organization_key === EMPTY_UUID ? '' : entity.organization_key,
  };
}

export function mapStudyToStudyEntity(study: Study): StudyEntity {
  return {
    key: study.key,
    name: study.name,
    description: study.description || '',
    region: study.region,
    c: {
      created_by: study.createdBy,
      created: study.createdOn,
      updated_by: study.updatedBy || '',
      updated: study.updatedOn || 0,
      update_reason: study.updateReason || '',
    },
    status: study.status,
    type: study.type,
    organization_key: study.organizationKey,
    configuration: study.configuration,
    row_state: study.state,
  };
}

export function mapSiteEntityToSite(entity: SiteEntity): Site {
  return {
    key: entity.key,
    name: entity.name,
    description: entity.description,
    createdBy: entity.c.created_by,
    createdOn: entity.c.created,
    updatedBy: entity.c.updated_by,
    updatedOn: entity.c.updated,
    updateReason: entity.c.update_reason,
    studyKey: entity.study_key === EMPTY_UUID ? '' : entity.study_key,
    region: entity.region,
    status: entity.status,
    state: entity.row_state,
    type: entity.type,
  };
}

export function mapSiteToSiteEntity(site: Site): SiteEntity {
  return {
    key: site.key,
    name: site.name,
    description: site.description || '',
    c: {
      created_by: site.createdBy,
      created: site.createdOn,
      updated_by: site.updatedBy || '',
      updated: site.updatedOn || 0,
      update_reason: site.updateReason || '',
    },
    study_key: site.studyKey,
    region: site.region,
    status: site.status,
    row_state: site.state,
    type: site.type,
  };
}

export function mapSubjectEntityToSubject(entity: SubjectEntity): Subject {
  return {
    key: entity.key,
    name: entity.name,
    description: entity.description,
    createdBy: entity.c.created_by,
    createdOn: entity.c.created,
    updatedBy: entity.c.updated_by,
    updatedOn: entity.c.updated,
    updateReason: entity.c.update_reason,
    siteKey: entity.site_key === EMPTY_UUID ? '' : entity.site_key,
    status: entity.status,
    region: entity.region,
    state: entity.row_state,
    type: entity.type,
    systemNotes: entity.system_notes.map((note) => ({
      ...note,
      timestamp: note.timestamp * 1000, // UI assumes milliseconds
    })),
    userNotes: entity.user_notes.map((note) => ({
      ...note,
      timestamp: note.timestamp * 1000, // UI assumes milliseconds
    })),
  };
}

export function mapSubjectToSubjectEntity(subject: Subject): SubjectEntity {
  return {
    key: subject.key,
    name: subject.name,
    description: subject.description || '',
    c: {
      created_by: subject.createdBy,
      created: subject.createdOn,
      updated_by: subject.updatedBy || '',
      updated: subject.updatedOn || 0,
      update_reason: subject.updateReason || '',
    },
    site_key: subject.siteKey,
    status: subject.status,
    region: subject.region,
    row_state: subject.state,
    type: subject.type,
    system_notes: subject.systemNotes.map((note) => ({
      ...note,
      timestamp: note.timestamp / 1000, // API expects seconds
    })),
    user_notes: subject.userNotes.map((note) => ({
      ...note,
      timestamp: note.timestamp / 1000, // API expects seconds
    })),
  };
}

export function mapDeviceEntityToDevice(entity: DeviceEntity): Device {
  return {
    key: entity.key,
    subjectKey: entity.subject_key === EMPTY_UUID ? '' : entity.subject_key,
    organizationKey:
      entity.organization_key === EMPTY_UUID ? '' : entity.organization_key,
    studyKey: entity.study_key === EMPTY_UUID ? '' : entity.study_key,
    siteKey: entity.site_key === EMPTY_UUID ? '' : entity.site_key,
    deviceId: entity.device_id,
    os: entity.os,
    osVersion: entity.osVersion,
    phoneModel: entity.phoneModel,
    phoneManufacturer: entity.phoneManufacturer,
    bundleId: entity.bundleId,
    bundleVersion: entity.bundleVersion,
    timezone: entity.timezone,
    state: entity.row_state,
    createdBy: entity.c.created_by,
    createdOn: entity.c.created,
    updatedBy: entity.c.updated_by,
    updatedOn: entity.c.updated,
    updateReason: entity.c.update_reason,
    appName: entity.appName,
    uniqueId: entity.uniqueId,
    status: entity.status,
  };
}

export function mapDeviceToDeviceEntity(device: Device): DeviceEntity {
  return {
    key: device.key,
    subject_key: device.subjectKey,
    organization_key: device.organizationKey,
    study_key: device.studyKey,
    site_key: device.siteKey,
    device_id: device.deviceId,
    os: device.os,
    osVersion: device.osVersion,
    phoneModel: device.phoneModel,
    phoneManufacturer: device.phoneManufacturer,
    bundleId: device.bundleId,
    bundleVersion: device.bundleVersion,
    timezone: device.timezone,
    c: {
      created: device.createdOn,
      created_by: device.createdBy,
      updated: device.updatedOn || 0,
      updated_by: device.updatedBy || '',
      update_reason: device.updateReason || '',
    },
    row_state: device.state || 0,
    appName: device.appName || '',
    uniqueId: device.uniqueId || '',
    status: device.status,
  };
}

export function mapSensorEntityToSensor(entity: SensorEntity): Sensor {
  return {
    key: entity.key,
    deviceKey: entity.device_key,
    serialNumber: entity.serialNumber,
    productType: entity.productType,
    warmupDuration: entity.warmupDuration,
    wearDuration: entity.wearDuration,
    activationTime: entity.activationTime * 1000, // UI assumes milliseconds
    firmwareVersion: entity.firmwareVersion,
    lastEvent: entity.lastEvent,
    analytes: entity.analytes || [],
    analyteCounts: entity.analyteCounts || [],
    conditionCode: entity.ClinicalSensorDetails.conditionCode,
    isPrimaryReceiver: entity.ClinicalSensorDetails.isPrimaryReceiver,
    isMdcCapable: entity.ClinicalSensorDetails.isMdcCapable,
    createdBy: entity.c.created_by,
    createdOn: entity.c.created,
    updatedBy: entity.c.updated_by,
    updatedOn: entity.c.updated,
    updateReason: entity.c.update_reason,
    sensorState: entity.state,
    state: entity.row_state,
    userNotes: entity.user_notes?.map((note) => ({
      ...note,
      timestamp: note.timestamp * 1000, // UI assumes milliseconds
    })),
    type: entity.type,
  };
}

export function mapSensorToSensorEntity(sensor: Sensor): SensorEntity {
  return {
    key: sensor.key,
    device_key: sensor.deviceKey,
    serialNumber: sensor.serialNumber,
    productType: sensor.productType,
    warmupDuration: sensor.warmupDuration,
    wearDuration: sensor.wearDuration,
    activationTime: sensor.activationTime / 1000, // API expects seconds
    firmwareVersion: sensor.firmwareVersion,
    lastEvent: sensor.lastEvent,
    analytes: sensor.analytes,
    analyteCounts: sensor.analyteCounts,
    ClinicalSensorDetails: {
      conditionCode: sensor.conditionCode,
      isPrimaryReceiver: sensor.isPrimaryReceiver,
      isMdcCapable: sensor.isMdcCapable,
    },
    c: {
      created: sensor.createdOn,
      created_by: sensor.createdBy,
      updated: sensor.updatedOn || 0,
      updated_by: sensor.updatedBy || '',
      update_reason: sensor.updateReason || '',
    },
    type: sensor.type,
    row_state: sensor.state,
    state: sensor.sensorState,
    user_notes: sensor.userNotes.map((note) => ({
      ...note,
      timestamp: note.timestamp / 1000, // API expects seconds
    })),
  };
}

export const mapProductTypeToString = (productType: ProductType): string => {
  switch (productType) {
    case ProductType.PRODUCT_TYPE_LIBRE1:
      return 'Libre 1';
    case ProductType.PRODUCT_TYPE_LIBRE2:
      return 'Libre 2';
    case ProductType.PRODUCT_TYPE_LIBRE2_SYSTEM_E:
      return 'Libre 2 System E';
    case ProductType.PRODUCT_TYPE_LIBRE3:
      return 'Libre 3';
    case ProductType.PRODUCT_TYPE_GLUCOSE_KETONE:
      return 'GKS';
    case ProductType.PRODUCT_TYPE_LACTATE_SPORT:
      return 'Lactate Sport';
    case ProductType.PRODUCT_TYPE_KETONE_DIET:
      return 'Ketone Diet';
    case ProductType.PRODUCT_TYPE_GLUCOSE_WELLNESS:
      return 'Glucose Wellness';
    default:
      return 'Unknown Product Type';
  }
};

export function mapStudyDataEntityToStudyDataRecord(
  entity: StudyDataEntity,
): StudyDataRecord {
  return {
    key: entity.key,
    processStatus: entity.process_status,
    systemNotes: entity.system_notes.map((note) => ({
      ...note,
      timestamp: note.timestamp * 1000, // UI assumes milliseconds
    })),
    createdBy: entity.c.created_by,
    createdOn: entity.c.created,
    updatedBy: entity.c.updated_by,
    updatedOn: entity.c.updated,
    updateReason: entity.c.update_reason,
  };
}

export function mapRoleEntityToRole(entity: RoleEntity): Role {
  return {
    key: entity.key,
    name: entity.name,
    description: entity.description,
    state: entity.row_state,
    createdBy: entity.c.created_by,
    createdOn: entity.c.created,
    updatedBy: entity.c.updated_by,
    updatedOn: entity.c.updated,
    updateReason: entity.c.update_reason,
  };
}

export function mapAccountEntityToAccount(entity: AccountEntity): Account {
  return {
    key: entity.key,
    firstName: entity.first_name,
    lastName: entity.last_name,
    username: entity.username || '',
    email: entity.email,
    country: entity.country || '',
    status: entity.status,
    state: entity.row_state,
    createdBy: entity.c.created_by,
    createdOn: entity.c.created,
    updatedBy: entity.c.updated_by,
    updatedOn: entity.c.updated,
    updateReason: entity.c.update_reason,
  };
}

export function mapAccountToAccountEntity(account: Account): AccountEntity {
  return {
    key: account.key,
    first_name: account.firstName,
    last_name: account.lastName,
    username: account.username,
    email: account.email,
    country: account.country,
    status: account.status,
    row_state: account.state,
    c: {
      created: account.createdOn,
      created_by: account.createdBy,
      updated: account.updatedOn,
      updated_by: account.updatedBy,
      update_reason: account.updateReason,
    },
  };
}

export function mapUserRoleAssignmentsByEntityToAccountsWithRoleAssignments(
  apiResponse: Array<UserRoleAssignmentsByEntity>,
): AccountWithRoleAssignments[] {
  return (apiResponse ?? []).flatMap((sourceEntity) =>
    (sourceEntity?.user_roles ?? []).flatMap((userRole) => {
      const user = userRole?.user;

      if (!user) {
        return [];
      }

      const roles = userRole.role ?? [];

      return {
        key: user.key,
        firstName: user.first_name,
        lastName: user.last_name,
        username: user.username || '',
        email: user.email,
        country: user.country || '',
        status: user.status,
        state: user.row_state,
        createdBy: '',
        createdOn: 0,
        updatedBy: '',
        updatedOn: 0,
        updateReason: '',
        roleAssignments: roles.map((role) => ({
          role: {
            key: role.key,
            name: role.name,
          },
          entityType: role.entity_type as EntityType,
          entityKey: sourceEntity.source_entity,
          entityName: '',
        })),
      };
    }),
  );
}

export function mapAccountRoleAssignmentsUpdateRequestToUserRoleAssignmentsByEntityUpdate(
  payload: AccountRoleAssignmentsUpdateRequest,
): UserRoleAssignmentsByEntityUpdate {
  return {
    source_entity: payload.sourceEntity,
    user_key: payload.userKey,
    add_role_keys: payload.addRoleKeys,
    remove_role_keys: payload.removeRoleKeys,
  };
}
